/**
 * Raise Alert feature — details of the alerts already raised against a study.
 *
 * Opened by clicking the "ALERT RAISED" badge in the studylist. Lists every
 * finding raised for the study, whether it was auto-detected or added by hand,
 * who raised it and when.
 *
 * Findings are loaded from study_critical_alert when the modal opens. A failed
 * load shows the empty state rather than an error — the studylist must keep
 * working regardless.
 */
import React, { useContext, useEffect, useState } from "react";
import { Icon, Spinner, SpinnerSize } from "@fluentui/react";
import { t } from "i18next";

import ModalMain from "../Modal/ModalMain";
import ModalHeader from "../Modal/ModalHeader";
import ModalFooter from "../Modal/ModalFooter";
import themeContext from "../../context/theme/ThemeContext";
import { PriorityChip, MutedChip, EvidenceList } from "./CriticalAlertBits";
import { getPriorityMeta } from "./criticalAlertConstants";
import { fetchStudyCriticalAlerts } from "../../service/CriticalAlertService";
import "./styles/CriticalAlertModals.css";

const PRIORITY_RANK = { CRITICAL: 0, URGENT: 1, HIGH_CONCERN: 2 };

/**
 * The five options the old Raise Alert dropdown offered, before this feature.
 * Mirrors Constants.RAISE_ALERT_LIST in the backend. Anything not listed here was
 * free text the radiologist typed, and is already readable as stored.
 */
const LEGACY_ALERT_LABELS = {
  pneumonia: "Pneumonia",
  suspicioncovid19: "Suspicion of COVID-19",
  consolidation: "Consolidation",
  pleuraleffusion: "Pleural effusion",
  pneumothorax: "Pneumothorax",
};

/**
 * @param {object} data  patient/study header details for the summary strip
 * @param {number} studyId  which study's findings to load
 * @param {string} legacyRaiseAlert  study.raise_alert as stored.
 *
 *   Studies reported BEFORE this feature have a raise_alert value but no rows in
 *   study_critical_alert, because that table did not exist. The badge is driven by
 *   raise_alert, so it still shows for them — without this the modal would open on
 *   "No findings recorded for this study", flatly contradicting the badge that
 *   opened it. We show the stored value instead, marked as a legacy entry, because
 *   the old flow recorded a single code and no priority, sentence or author.
 */
const RaisedAlertDetailsModal = ({
  isOpen,
  onClose,
  data = {},
  studyId,
  legacyRaiseAlert = null,
}) => {
  const { getThemeColor } = useContext(themeContext);
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(false);

  const primaryColor = getThemeColor("primary-color");
  const rowBg = getThemeColor("popup-background");
  const rowBorder = getThemeColor("border");

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;
    setLoading(true);

    fetchStudyCriticalAlerts(studyId)
      .then((rows) => {
        if (cancelled) {
          return;
        }
        const sorted = [...(rows || [])].sort(
          (a, b) =>
            (PRIORITY_RANK[a.priority] ?? 9) - (PRIORITY_RANK[b.priority] ?? 9)
        );
        setFindings(sorted);
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, studyId]);

  if (!isOpen) {
    return null;
  }

  /* Only used when the child table has no rows for this study. Known codes get
     their old dropdown label; free text the radiologist typed is shown as-is. */
  const legacyKey = (legacyRaiseAlert || "").trim();
  const legacyAlertLabel = legacyKey
    ? LEGACY_ALERT_LABELS[legacyKey.toLowerCase()] || legacyKey
    : null;

  const summaryFields = [
    { label: t("study-list.table-header.patient-name", "Patient name"), value: data?.patientName },
    { label: t("string-literals.patient-id", "Patient ID"), value: data?.patientId },
    { label: t("study-list.table-header.study-desc", "Study description"), value: data?.studyDescription },
    { label: t("study-list.table-header.study-date", "Study date"), value: data?.studyDate },
  ];

  const modalDetails = {
    width: "600px",
    maxWidth: "94vw",
    header: (
      <ModalHeader
        updatedModal={true}
        heading={t("study-list.badges.raise-alert", "Alert Raised")}
        handleCancel={onClose}
      />
    ),
    body: (
      <div className="ca-modal-body" style={{ color: primaryColor }}>
        <div className="ca-study-summary" style={{ borderBottomColor: rowBorder }}>
          {summaryFields.map((field) => (
            <div key={field.label}>
              <p className="ca-summary-label">{field.label}</p>
              <p className="ca-summary-value">{field.value || "—"}</p>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="ca-empty">
            <Spinner size={SpinnerSize.medium}
              label={t("critical-alert.loading", "Loading findings…")} />
          </div>
        ) : findings.length === 0 && legacyAlertLabel ? (
          /* Reported before this feature: study.raise_alert holds a value but the
             child table has no rows. Show what was actually stored rather than
             "nothing recorded", which would contradict the badge. */
          <>
            <ul className="ca-list">
              <li className="ca-row" style={{ background: rowBg, borderColor: rowBorder }}>
                <div className="ca-row-main">
                  <span className="ca-row-name">{legacyAlertLabel}</span>
                  <span
                    className="ca-chip"
                    style={{ color: rowBorder, borderColor: rowBorder }}
                  >
                    {t("critical-alert.legacy-tag", "Recorded earlier")}
                  </span>
                </div>
              </li>
            </ul>
            <p className="ca-empty">
              {t(
                "critical-alert.legacy-note",
                "This alert was raised before detailed findings were recorded, so only the selected reason is available."
              )}
            </p>
          </>
        ) : findings.length === 0 ? (
          <p className="ca-empty">
            {t("critical-alert.no-findings", "No findings recorded for this study.")}
          </p>
        ) : (
          <ul className="ca-list">
            {findings.map((finding, index) => {
              const meta = getPriorityMeta(finding.priority);
              const isManual = finding.source === "MANUAL";
              return (
                <li
                  key={`${finding.alertCode}-${index}`}
                  className="ca-row"
                  style={{
                    background: rowBg,
                    borderColor: rowBorder,
                    borderLeftColor: meta.color,
                  }}
                >
                  <div className="ca-row-main">
                    <div className="ca-row-title-line">
                      <span
                        className="ca-finding-name"
                        style={{ color: primaryColor }}
                      >
                        {finding.findingName}
                      </span>
                      <PriorityChip priority={finding.priority} />
                      <MutedChip
                        text={
                          isManual
                            ? t("critical-alert.added-manually", "Added manually")
                            : t("critical-alert.auto-detected", "Auto-detected")
                        }
                        color={primaryColor}
                      />
                    </div>

                    <EvidenceList
                      occurrences={finding.occurrences}
                      sentence={finding.matchedSentence}
                      matchedTerm={finding.matchedTerm}
                      accentColor={meta.color}
                      collapsed
                    />

                    <p className="ca-meta-line">
                      <span className="ca-meta-item">
                        <Icon iconName="Contact" style={{ fontSize: 11 }} />
                        {finding.raisedBy}
                      </span>
                      <span className="ca-meta-item">
                        <Icon iconName="Clock" style={{ fontSize: 11 }} />
                        {finding.raisedAt}
                      </span>
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    ),
    footer: (
      <ModalFooter
        PrimaryButtonList={[]}
        DefaultButtonList={[
          { title: t("button.close", "Close"), onclick: onClose },
        ]}
      >
        <div className="ca-footer-summary" style={{ color: primaryColor }}>
          <span className="ca-footer-count">
            {t("critical-alert.total-raised", "Total findings raised")}:{" "}
            <strong>{findings.length}</strong>
          </span>
        </div>
      </ModalFooter>
    ),
  };

  return (
    <ModalMain
      updatedModal={true}
      showModal={isOpen}
      scrollableBody={true}
      modalDetails={modalDetails}
    />
  );
};

export default RaisedAlertDetailsModal;